import { motion } from "framer-motion";
import { Phone, Mail, MapPin } from "lucide-react";
import { contactInfo } from "../data/staticData";

// Icono de WhatsApp
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Contact = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hola, me gustaría obtener información sobre vuestros servicios de detailing.");
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${message}`, "_blank");
  };

  const handleEmailClick = () => {
    const subject = encodeURIComponent("Consulta sobre servicios de detailing");
    const body = encodeURIComponent("Hola,\n\nMe gustaría obtener información sobre vuestros servicios de detailing.\n\nGracias.");
    window.location.href = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black uppercase tracking-tighter mb-4 sm:mb-6 px-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-white">Contáctanos y </span>
            <span className="text-[#00A8FF] text-glow">transforma</span>
            <br />
            <span className="text-white">tu vehículo</span>
          </motion.h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tighter mb-4 sm:mb-8">
                <span className="text-white">Información de </span>
                <span className="text-[#00A8FF]">Contacto</span>
              </h2>

              <div className="space-y-4 sm:space-y-6 mb-8 sm:mb-12">
                <motion.a
                  href={`tel:${contactInfo.phone}`}
                  className="glass-card p-4 sm:p-6 flex items-center gap-4 sm:gap-6 group hover:border-[#00A8FF]/50 transition-all"
                  whileHover={{ scale: 1.02, x: 10 }}
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 glass rounded-lg flex items-center justify-center group-hover:glow-blue transition-all flex-shrink-0">
                    <Phone className="w-6 h-6 sm:w-8 sm:h-8 text-[#00A8FF]" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs sm:text-sm uppercase tracking-wider mb-1">Teléfono</p>
                    <p className="text-lg sm:text-xl lg:text-2xl font-bold text-white group-hover:text-[#00A8FF] transition-colors">
                      {contactInfo.phoneFormatted}
                    </p>
                  </div>
                </motion.a>

                <motion.a
                  href={`mailto:${contactInfo.email}`}
                  className="glass-card p-4 sm:p-6 flex items-center gap-4 sm:gap-6 group hover:border-[#00A8FF]/50 transition-all"
                  whileHover={{ scale: 1.02, x: 10 }}
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 glass rounded-lg flex items-center justify-center group-hover:glow-blue transition-all flex-shrink-0">
                    <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-[#00A8FF]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white/60 text-xs sm:text-sm uppercase tracking-wider mb-1">Email</p>
                    <p className="text-sm sm:text-base lg:text-xl font-bold text-white group-hover:text-[#00A8FF] transition-colors break-all">
                      {contactInfo.email}
                    </p>
                  </div>
                </motion.a>

                <motion.div
                  className="glass-card p-4 sm:p-6 flex items-start gap-4 sm:gap-6"
                  whileHover={{ scale: 1.02, x: 10 }}
                  data-testid="contact-address"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 glass rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 sm:w-8 sm:h-8 text-[#00A8FF]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white/60 text-xs sm:text-sm uppercase tracking-wider mb-1">Dirección</p>
                    <p className="text-sm sm:text-base lg:text-lg font-medium text-white">
                      {contactInfo.address}
                    </p>
                    <p className="text-white/60 text-sm sm:text-base">
                      {contactInfo.city}
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Map */}
              <motion.div
                className="glass-card overflow-hidden"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                data-testid="contact-map"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3031.7123440841287!2d-3.4813793!3d40.5247505!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd42346a42971903%3A0xb9a293038d8fd312!2sPol.%20Ind.%20Juan%20y%20Antonio%2C%20Pol%C3%ADgono%20Cubilete%2C%2018%2C%2028864%20Ajalvir%2C%20Madrid!5e0!3m2!1ses!2ses!4v1699999999999!5m2!1ses!2ses"
                  width="100%"
                  height="300"
                  style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación Brillox"
                />
              </motion.div>
            </motion.div>

            {/* Contact Buttons */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tighter mb-4 sm:mb-8">
                <span className="text-white">¿Listo para </span>
                <span className="text-[#00A8FF]">empezar?</span>
              </h2>

              <div className="glass-card p-6 sm:p-8 lg:p-12" data-testid="contact-buttons-section">
                <p className="text-white/70 text-sm sm:text-base lg:text-lg font-light leading-relaxed mb-8">
                  Elige cómo prefieres contactarnos. Estaremos encantados de atenderte y resolver todas tus dudas sobre nuestros servicios de detailing premium.
                </p>

                <div className="space-y-6">
                  {/* Email Button */}
                  <motion.button
                    onClick={handleEmailClick}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full btn-skew bg-[#00A8FF] text-[#050505] py-5 font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,168,255,0.5)] transition-all duration-300 flex items-center justify-center gap-3"
                    data-testid="contact-email-btn"
                  >
                    <Mail className="w-6 h-6" />
                    <span>Contáctame por Email</span>
                  </motion.button>

                  {/* WhatsApp Button */}
                  <motion.button
                    onClick={handleWhatsAppClick}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full btn-skew bg-[#25D366] text-white py-5 font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] transition-all duration-300 flex items-center justify-center gap-3"
                    data-testid="contact-whatsapp-btn"
                  >
                    <WhatsAppIcon className="w-6 h-6" />
                    <span>Contáctame por WhatsApp</span>
                  </motion.button>
                </div>

                {/* Additional info */}
                <div className="mt-8 pt-8 border-t border-white/10">
                  <p className="text-white/50 text-sm text-center">
                    Respondemos en menos de 24 horas
                  </p>
                </div>
              </div>

              {/* Hours info */}
              <motion.div
                className="glass-card p-6 mt-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-lg font-bold text-white mb-4">Horario de Atención</h3>
                <div className="space-y-2 text-white/70">
                  <div className="flex justify-between">
                    <span>Lunes - Viernes</span>
                    <span className="text-[#00A8FF]">8:30 - 19:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sábado</span>
                    <span className="text-[#00A8FF]">10:00 - 15:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Domingo</span>
                    <span className="text-white/50">Cerrado</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
